import { join } from "node:path";
import type { Logger } from "../utils/logger.js";
import type { Url, ResumeToken, AdapterId, ChapterIndex } from "../data-model/primitives.js";
import type { ResumeOptions } from "../data-model/options.js";
import type { Chapter } from "../data-model/novel.js";
import { ParseError } from "../core/errors.js";
import { freshState, type ResumeState } from "./resume-state.js";
import { sha1, userCacheDir } from "./_fs.js";
import { FilesystemResumeStore, MemoryResumeStore, type ResumeStore } from "./resume-store.js";
import { FilesystemChapterStore, MemoryChapterStore, type ChapterStore } from "./chapter-store.js";
import { verifyResumeToken } from "./resume-token.js";

/**
 * Owns resume persistence: the state-file path plus the `${stateFile}.chapters`
 * directory convention, and the three resume-option entry points. The cache
 * store is intentionally NOT here — it is keyed by the caller's cache dir and
 * wired separately in Downloader.
 */
export class ResumeStorage {
  readonly resume: ResumeStore;
  readonly chapters: ChapterStore;
  readonly stateFile: string;

  private constructor(resume: ResumeStore, chapters: ChapterStore, stateFile: string) {
    this.resume = resume;
    this.chapters = chapters;
    this.stateFile = stateFile;
  }

  static forDefault(url: Url, logger: Logger): ResumeStorage {
    const stateFile = join(userCacheDir(), "webnovel-downloader", "state", `${sha1(url)}.json`);
    return ResumeStorage.#filesystem(stateFile, logger);
  }

  static forStateFile(stateFile: string, logger: Logger): ResumeStorage {
    return ResumeStorage.#filesystem(stateFile, logger);
  }

  static forToken(token: ResumeToken, url: Url, logger: Logger): ResumeStorage {
    const { stateFile } = verifyResumeToken(token, url);
    return ResumeStorage.#filesystem(stateFile, logger);
  }

  static memory(stateFile: string): ResumeStorage {
    return new ResumeStorage(new MemoryResumeStore(), new MemoryChapterStore(), stateFile);
  }

  static #filesystem(stateFile: string, logger: Logger): ResumeStorage {
    return new ResumeStorage(
      new FilesystemResumeStore(),
      new FilesystemChapterStore(`${stateFile}.chapters`, { logger }),
      stateFile,
    );
  }

  /**
   * Loads state for a run against (url, adapterId) and applies the reconcile
   * invariant: a chapter file on disk proves the fetch completed, so its index
   * wins over a stale `state.completed` (they diverge when run 1's in-flight
   * tasks write chapters after abort but before the debounced state save).
   */
  async loadFor(
    url: Url,
    adapterId: AdapterId,
  ): Promise<{ state: ResumeState; priorChapters: readonly Chapter[] }> {
    let state = await this.resume.load(this.stateFile);
    if (state) {
      if (state.url !== url) {
        throw new ParseError("Stale resume state: URL mismatch", { url, path: this.stateFile });
      }
      if (state.adapterId !== adapterId) {
        throw new ParseError("Stale resume state: adapter mismatch", { url, path: this.stateFile });
      }
    } else {
      state = freshState(url, adapterId);
    }
    const priorChapters = await this.chapters.loadAll();
    if (priorChapters.length > 0) {
      const merged = new Set<ChapterIndex>(state.completed);
      for (const c of priorChapters) merged.add(c.index);
      state = { ...state, completed: [...merged].sort((a, b) => Number(a) - Number(b)) };
    }
    return { state, priorChapters };
  }
}

export function resolveResumeStorage(
  opt: ResumeOptions | undefined,
  url: Url,
  logger: Logger,
): ResumeStorage | null {
  if (opt === undefined) return null;
  if (opt === true) return ResumeStorage.forDefault(url, logger);
  if ("stateFile" in opt) return ResumeStorage.forStateFile(opt.stateFile, logger);
  if ("token" in opt) return ResumeStorage.forToken(opt.token, url, logger);
  return null;
}
